import {
  createPdpAiLayoutInCurrentLayout,
  createPdpLayeredLayoutInCurrentLayout,
} from '@frontend/components/modules/layouts-page/EditorControl/components/AIChat/pdp/sidebar-pdp-layout-creation';

/**
 * Layout sidebar PDP adapter — component-backed implementation of the
 * chat-surface contract consumed by the shared PDP handlers in
 * resources/js/frontend/store/creative-studio/chat-function-handlers/.
 *
 * The sidebar keeps chat state in `data()` and persists via localStorage
 * (see AIEditorTab.vue saveMessages), so the adapter maps adapter methods
 * onto component instance methods and reactive fields.
 */
export function createSidebarPdpAdapter(vm) {
  const adapter = {
    getMessages: () => vm.messages,
    getParsedPrompt: () => vm.pdpParsedPrompt || {},
    setParsedPrompt: (parsed) => {
      vm.pdpParsedPrompt = { ...(parsed || {}) };
    },
    addMessage: (msg) => vm.pushMessage(msg),
    updateMessage: (index, patch) => {
      const target = vm.messages[index];
      if (!target) return;
      Object.entries(patch).forEach(([key, value]) => {
        vm.$set(target, key, value);
      });
    },
    setMessageDisabled: (index, disabled) => {
      const target = vm.messages[index];
      if (!target) return;
      vm.$set(target, 'disabled', disabled);
    },
    saveMessages: async () => vm.saveMessages(),
    saveConversationState: async () => vm.saveMessages(),
    addAndSaveMessage: async (msg) => {
      vm.pushMessage(msg);
      await vm.saveMessages();
      return msg;
    },
    addAndSaveMessages: async (messages = []) => {
      messages.forEach((msg) => vm.pushMessage(msg));
      await vm.saveMessages();
      return messages;
    },
    updateAndSaveMessage: async (index, patch) => {
      adapter.updateMessage(index, patch);
      await vm.saveMessages();
      return vm.messages[index];
    },
    setProcessing: (flag) => {
      if (flag) {
        vm.startLoading();
      } else {
        vm.stopLoading();
      }
    },
    setPendingRegeneration: (payload) => {
      vm.pendingPdpRegeneration = payload;
    },
    setChatText: (text) => {
      vm.userInput = text;
      if (vm.$refs && vm.$refs.chatTextArea && typeof vm.$refs.chatTextArea.setSuggestion === 'function') {
        vm.$refs.chatTextArea.setSuggestion(text);
      }
    },
    setShowcaseTemplates: () => {
      // The sidebar does not render a template showcase panel; the template
      // gallery widget embedded in the chat message already carries the
      // templates it needs.
    },
    pushPendingDesignJob: () => {
      // The editor sidebar does not support cross-page recovery; the chat
      // state lives in the layout editor component and disappears when the
      // user navigates away. No-op so the shared handler can safely call it.
    },
    resolvePendingDesignJob: () => {
      // No-op for the same reason as pushPendingDesignJob.
    },
    getSource: () => 'creative_studio_editor',
    supportsReferenceRowPicker: () => false,
    getCurrentLayoutId: () => {
      const el = document.getElementById('template-group-preview');
      return el ? el.getAttribute('data-layout-id') : null;
    },
    refreshLayout: async () => vm.reloadLayoutAfterPdpAppend(),
    navigateToLayout: () => {
      // No-op: the sidebar user is already on the layout page.
    },
    dispatch: () => Promise.resolve(),
  };

  // Skips the "Create editable layout" confirmation step (used by the
  // Finish flow) — runs the same handler the confirm button would have
  // triggered. Mirrors AIEditorTab's onWidgetAction for the create_pdp_*
  // actions.
  adapter.triggerPdpLayoutCreation = (messageIndex, pathType) => {
    adapter.setMessageDisabled(messageIndex, true);
    if (pathType === 'ai_generate') {
      createPdpAiLayoutInCurrentLayout(adapter, messageIndex);
    } else {
      createPdpLayeredLayoutInCurrentLayout(adapter, messageIndex);
    }
  };

  return adapter;
}
